import { Activity, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { subscribeJobEvents } from '../api/sse.js';
import type { JobEvent, JobRecord } from '../api/types.js';
import { useToken } from '../auth/use-token.js';

interface JobProgressProps {
  readonly jobId: string;
  readonly onTerminal?: (job: JobRecord | null) => void;
}

export function JobProgress({ jobId, onTerminal }: JobProgressProps): JSX.Element {
  const getToken = useToken();
  const [job, setJob] = useState<JobRecord | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setJob(null);
    setLogs([]);
    setError(null);
    const stop = subscribeJobEvents(jobId, getToken, {
      onEvent: (event: JobEvent) => {
        if (event.type === 'log') {
          if (event.message) setLogs((prev) => [...prev.slice(-49), event.message ?? '']);
          return;
        }
        if (event.job) setJob(event.job);
        if (event.type === 'completed' || event.type === 'failed') {
          onTerminal?.(event.job ?? null);
        }
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : String(err));
      },
    });
    return stop;
  }, [jobId, getToken, onTerminal]);

  if (error && !job) {
    return (
      <p className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
        SSE error: {error}
      </p>
    );
  }
  if (!job) {
    return (
      <p className="flex items-center gap-2 font-mono text-xs text-text-secondary">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
        Connecting…
      </p>
    );
  }

  const pct = Math.round((job.progress ?? 0) * 100);
  return (
    <div className="grid gap-2.5">
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-accent" />
          <strong className="font-mono text-text-primary">{job.slug}</strong>
          <span className="rounded bg-elevated px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
            {job.status}
          </span>
        </span>
        <span className="font-mono text-text-primary">{pct}%</span>
      </div>
      <div className="relative h-2 overflow-hidden rounded-full bg-elevated">
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent to-fuchsia-500 transition-all duration-300"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
        {pct < 100 && job.status !== 'failed' && (
          <div className="shimmer-bar absolute inset-0" aria-hidden />
        )}
      </div>
      {job.message && <p className="text-xs text-text-secondary">{job.message}</p>}
      {logs.length > 0 && (
        <pre className="scrollbar-thin max-h-44 overflow-auto rounded-lg border border-border-subtle bg-deep p-3 font-mono text-[11px] leading-relaxed text-text-secondary">
          {logs.join('\n')}
        </pre>
      )}
    </div>
  );
}
